// FeatureContext - per-user feature flags from the profilemgr backend (/api/features/available).
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { AuthContext } from './AuthContext';

const API_URL = process.env.REACT_APP_API_URL;

const FeatureContext = createContext({
  features: [],
  loading: true,
  isFeatureEnabled: () => true,
  getFeatureOptions: () => ({}),
});

export const FeatureProvider = ({ children }) => {
  const { isAuthenticated, currentUser } = useContext(AuthContext);
  const [features, setFeatures] = useState(null); // null = not configured / not loaded
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      setFeatures(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    axios
      .get(`${API_URL}/api/features/available`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      })
      .then(({ data }) => setFeatures(Array.isArray(data) && data.length ? data : null))
      .catch((err) => {
        console.error('Could not load features:', err);
        setFeatures(null);
      })
      .finally(() => setLoading(false));
  }, [isAuthenticated, currentUser?.email]);

  const findFeature = useCallback(
    (name) => features?.find((f) => f.component_name === name || f.name === name),
    [features]
  );

  // With no feature configuration in the database, everything stays enabled
  const isFeatureEnabled = useCallback(
    (name) => (features ? !!findFeature(name) : true),
    [features, findFeature]
  );

  const getFeatureOptions = useCallback((name) => findFeature(name)?.options || {}, [findFeature]);

  return (
    <FeatureContext.Provider value={{ features: features || [], loading, isFeatureEnabled, getFeatureOptions }}>
      {children}
    </FeatureContext.Provider>
  );
};

export const useFeatures = () => useContext(FeatureContext);

export default FeatureContext;
